import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { BaseModal } from 'src/design/molecules/BaseModal';
import { sortBy, sumBy } from 'lodash';
import groupBy from 'lodash/groupBy';
import React from 'react';
import { TreeNodeData } from '@mantine/core';
import { Box } from 'src/design/atoms/Box';
import { Grid } from 'src/design/atoms/Grid';
import { Text } from 'src/design/atoms/Text';
import { Tree } from 'src/design/molecules/Tree';
import { useCtcMaterialsForCode } from 'src/api/queries/ctc/useCtcMaterials';
import { formatCurrency } from 'src/design/atoms/Currency';

interface MaterialsDialogProps {
  handleClose: () => void;
  open: boolean;
  jobNumber: string;
  costCode: string | null;
  description: string | null;
  isMtd?: boolean;
  totalValue?: number;
  totalRemaining: number;
  poNumber: string | null;
}

export const MaterialsDialog: React.FC<MaterialsDialogProps> = ({
  handleClose,
  costCode,
  open,
  jobNumber,
  description,
  poNumber,
}) => {
  const { data, isLoading } = useCtcMaterialsForCode(jobNumber, costCode, open);
  const material = React.useMemo(() => data ?? {}, [data]);

  const treeData = React.useMemo((): TreeNodeData[] => {
    let totalRemFinal = 0;
    let totalCost = 0;
    const nodes: TreeNodeData[] = [];

    sortBy(Object.keys(material)).forEach((key) => {
      const values = material[key];
      const numberValues = values.map((val) => ({
        ...val,
        unitcostNumber: Number(val.unitcost),
        remainingNumber: Number(val.remainingPoBalance),
      }));
      const poGroup = groupBy(values, 'ponumber');
      let remainTotal = 0;
      Object.keys(poGroup).forEach((groupKey) => {
        const vals = poGroup[groupKey];
        const tmp = vals[0] ? Number(vals[0].remainingPoBalance) : 0;
        remainTotal = remainTotal + tmp;
      });
      if (Math.round(remainTotal) === 0) return;

      const children: TreeNodeData[] = [];
      Object.keys(poGroup).forEach((k) => {
        const vals = poGroup[k].map((i) => ({
          ...i,
          remainingPoBalance: Number(i.remainingPoBalance),
          poAmount: Number(i.poAmount),
          unitcost: Number(i.unitcost),
        }));

        const poTotal = sumBy(vals, 'poAmount');
        const rTotal = vals[0].remainingPoBalance;
        totalCost = totalCost + poTotal;
        totalRemFinal = totalRemFinal + rTotal;
        if (Math.round(rTotal) === 0) return;

        const leafChildren: TreeNodeData[] = vals
          .filter((val) => val.poLineStatus !== 'Closed')
          .map((val) => ({
            value: String(val.id),
            label: `${val.itemdesc.length > 0 ? val.itemdesc : '-'} ${
              val.poLineStatus.length > 0 ? `(${val.poLineStatus})` : ''
            }`,
            nodeProps: {
              amount: formatCurrency(val.unitcost * Number(val.qtyorder), {
                precise: true,
              }),
            },
          }));

        children.push({
          value: k,
          label: k,
          nodeProps: { remaining: formatCurrency(rTotal, { precise: true }) },
          children: leafChildren,
        });
      });

      nodes.push({
        value: key,
        label: key.length === 0 ? 'No Description' : key,
        children,
      });
    });

    return nodes;
  }, [material]);

  return (
    <BaseModal
      opened={open}
      onClose={handleClose}
      title={`Materials - ${costCode ? costCode.toUpperCase() : ''}`}
      size="xl"
    >
      <Box p="md" pt={0}>
        {isLoading && <p style={{ paddingLeft: 10 }}>Loading...</p>}
        {Object.keys(material).length > 0 ? (
          <Box style={{ height: 400, overflow: 'auto' }}>
            <Grid mb="xs">
              <Grid.Col span={6}>
                <Text fw={700} size="sm">
                  Description
                </Text>
              </Grid.Col>
              <Grid.Col span="auto" ta="right">
                <Text fw={700} size="sm">
                  Remaining
                </Text>
              </Grid.Col>
            </Grid>
            <Tree
              data={treeData}
              expandOnClick
              renderNode={({ node, expanded, hasChildren, elementProps }) => (
                <Box {...elementProps}>
                  <Box
                    style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    {hasChildren &&
                      (expanded ? (
                        <IconChevronDown size={16} />
                      ) : (
                        <IconChevronRight size={16} />
                      ))}
                    <Text size="sm" style={{ textTransform: 'capitalize' }}>
                      {node.label}
                    </Text>
                    {(node.nodeProps as any)?.remaining && (
                      <Text size="sm" ml="auto" fw={700}>
                        {(node.nodeProps as any).remaining}
                      </Text>
                    )}
                    {(node.nodeProps as any)?.amount && (
                      <Text size="sm" ml="auto">
                        {(node.nodeProps as any).amount}
                      </Text>
                    )}
                  </Box>
                </Box>
              )}
            />
          </Box>
        ) : (
          <p>No items...</p>
        )}
      </Box>
    </BaseModal>
  );
};
